import { VERIFICATION_STATUES } from '@/constants';
import * as dataSelectors from '@/data/data-selectors';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { StyledButton } from '../../listing-info-title-styles';
import { ListingVerificationModal } from './verification-modal';

const Button = styled(StyledButton).attrs(() => ({
  size: 'sm',
}))`
  height: 100%;
`;

function ListingVerificationButtonComponent(props) {
  const { enableListingPopover, listing, propertyData } = props;

  const listingIsUnverified =
    listing.verifiedStatus === VERIFICATION_STATUES.UNVERIFIED;

  /**
   * The feature is disabled for the listing while under review, so if the feature is active
   * the "verifiedStatus" field of all listings can only be:
   *    1. "verified" (VERIFICATION_STATUES.VERIFIED)
   *    or
   *    2. "unverified" (VERIFICATION_STATUES.UNVERIFIED)
   */
  const listingIsPendingReview =
    listing.verifiedStatus === VERIFICATION_STATUES.PENDING_VERIFICATION;

  // The listing has no property identifying information (apn and address).
  const listingIsUnidentified =
    isNil(propertyData?.apn) && isNil(propertyData?.address);

  const onClick = useCallback(() => {
    if (listingIsPendingReview) return;
    props.setActiveDeckardId(listing.deckardid);
  }, [listing.deckardid, listingIsPendingReview, props]);

  const onClose = useCallback(() => {
    props.setActiveDeckardId('');
  }, [props]);

  const buttonLabel = useMemo(() => {
    /**
     * For an "unknown" (listing is neither verified nor has property identifying information) listing,
     * the user is only able to suggest an address instead of both an apn and an address.
     */
    if (listingIsUnverified && listingIsUnidentified) return 'Address';

    /**
     * Unverified listing with a probable apn and address for the property can perform the "verify" action.
     * Verified listings are restricted to disputing the probable/identified apn and address.
     * Pending listings display "Dispute", but cannot be acted upon while a review is pending.
     */
    return listingIsUnverified ? 'Verify' : 'Dispute Address';
  }, [listingIsUnidentified, listingIsUnverified]);

  return (
    <div>
      <Button
        aria-label="Open form to dispute address for listing"
        onClick={onClick}
        disabled={listingIsPendingReview}
      >
        {buttonLabel}
      </Button>

      {enableListingPopover && (
        <ListingVerificationModal
          modalTitle={buttonLabel}
          show={enableListingPopover}
          onClose={onClose}
          propertyData={propertyData}
          listing={listing}
          listingIsUnidentified={listingIsUnidentified}
        />
      )}
    </div>
  );
}

function mapStateToProps(state) {
  return {
    propertyData: dataSelectors.propertyData(state),
  };
}

export const ListingVerificationButton = connect(mapStateToProps)(
  ListingVerificationButtonComponent
);

ListingVerificationButton.propTypes = {
  setActiveDeckardId: PropTypes.func.isRequired,
  enableListingPopover: PropTypes.bool.isRequired,
  listing: PropTypes.object.isRequired,
  propertyData: PropTypes.object,
};
